import { Button, HStack, Box, Text, VStack, Flex } from '@chakra-ui/react';
import React from 'react';
import { PieChart, Pie, Legend } from 'recharts';

import { utils as commonUtils } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankReceivableStats,
  ValleyBankLoanAccountsStats,
} from '@finley-technologies/valley-bank-utils';

import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';

const LOAN_ACCOUNTS = 'loanAccounts' as const;
const COLLATERAL_ACCOUNTS = 'collateralAccounts' as const;

type TabType = typeof LOAN_ACCOUNTS | typeof COLLATERAL_ACCOUNTS;

type CollateralAccountStats = Pick<
  ValleyBankReceivableStats,
  | 'totalPreAllocationLendableValue'
  | 'totalMarketValue'
  | 'effectiveAdvanceRate'
>;

type CollateralAccountProps = {
  assigned: CollateralAccountStats;
  unassigned: CollateralAccountStats;
};

type LoanAccountStats = Pick<
  ValleyBankLoanAccountsStats,
  | 'excessMarginStatusCount'
  | 'loanAccountsCount'
  | 'nearMarginStatusCount'
  | 'totalOutstandingBalance'
  | 'totalMarginAvailability'
  | 'totalMarginAvailabilityRate'
  | 'underMarginStatusCount'
>;

export type LoanAndCollateralAccountsStatsComponentProps = {
  collateralStats: CollateralAccountProps;
  loanStats?: LoanAccountStats;
};

const handlePlural = ({
  count,
  singular,
  plural,
}: {
  count: number;
  singular: string;
  plural: string;
}) => (count === 1 ? singular : plural);

type StatsPanelWrapperProps = {
  graph: React.ReactElement | null;
  cardsSection: React.ReactElement;
};
const StatsPanelWrapper = ({ graph, cardsSection }: StatsPanelWrapperProps) => {
  return (
    <HStack
      w="100%"
      justify="flex-start"
      p="40px"
      spacing="40px"
      bgColor={colors.millet[50]}
      h="100%"
    >
      {graph && (
        <Box w="fit-contents" h="100%">
          {graph}
        </Box>
      )}
      <Box w="100%" h="100%">
        {cardsSection}
      </Box>
    </HStack>
  );
};

/**
 * ========================================
 * *********COLLATERAL ACCOUNTS************
 * ========================================
 */

type CollateralAccountCardRowProps = {
  header: string;
} & CollateralAccountStats;
const CollateralAccountCardRow = ({
  header,
  totalPreAllocationLendableValue,
  totalMarketValue,
  effectiveAdvanceRate,
}: CollateralAccountCardRowProps) => {
  return (
    <>
      <Text fontSize="16px" fontWeight="600">
        {header}
      </Text>
      <HStack w="100%" spacing="16px">
        <InfoCard
          title="Total Market Value"
          value={formatCurrencyAmount(totalMarketValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Blended Advance Rate"
          value={percentageToPercentageLabel(effectiveAdvanceRate * 100)}
          icon={<Icon icon="percentage" size="24px" />}
          flexGrow={1}
        />
        <InfoCard
          title="Total Lendable Value"
          value={formatCurrencyAmount(
            totalPreAllocationLendableValue,
            Bank.Currency.USD,
          )}
          icon={<Icon icon="dollar" size="24px" />}
          flexGrow={1}
        />
      </HStack>
    </>
  );
};

const CollateralAccountsPanel = ({
  assigned,
  unassigned,
}: CollateralAccountProps) => {
  const totalLendableValue =
    assigned.totalPreAllocationLendableValue +
    unassigned.totalPreAllocationLendableValue;

  const assignedLendableValuePercentage = commonUtils.divideByOrZero(
    assigned.totalPreAllocationLendableValue,
    totalLendableValue,
  );

  const unassignedLendableValuePercentage = commonUtils.divideByOrZero(
    unassigned.totalPreAllocationLendableValue,
    totalLendableValue,
  );

  return (
    <StatsPanelWrapper
      graph={
        assignedLendableValuePercentage > 0 &&
        unassignedLendableValuePercentage > 0 ? (
          <PieChart width={320} height={400}>
            <Pie
              width={160}
              height={160}
              data={[
                {
                  name: `${percentageToPercentageLabel(
                    assignedLendableValuePercentage * 100,
                    1,
                  )} Assigned Lendable Value`,
                  value: assigned.totalPreAllocationLendableValue,
                  fill: colors.orange[200],
                },
                {
                  name: `${percentageToPercentageLabel(
                    unassignedLendableValuePercentage * 100,
                    1,
                  )} Unassigned Lendable Value`,
                  value: unassigned.totalPreAllocationLendableValue,
                  fill: colors.orange[75],
                },
              ]}
              dataKey="value"
              nameKey="name"
            />
            <Legend
              verticalAlign="bottom"
              layout="vertical"
              iconType="circle"
              iconSize={8}
              formatter={(value) => (
                <Text color={colors.black[70]} display="inline-block" ml="8px">
                  {value}
                </Text>
              )}
            />
          </PieChart>
        ) : null
      }
      cardsSection={
        <VStack spacing="24px" alignItems="flex-start">
          <CollateralAccountCardRow {...assigned} header="Assigned" />
          <CollateralAccountCardRow {...unassigned} header="Unassigned" />
        </VStack>
      }
    />
  );
};

/**
 * ========================================
 * *************LOAN ACCOUNTS**************
 * ========================================
 */

// TODO get rid of mock data
const LoanAccountsPanel = ({
  excessMarginStatusCount,
  nearMarginStatusCount,
  underMarginStatusCount,
  totalOutstandingBalance,
  loanAccountsCount,
  totalMarginAvailability,
  totalMarginAvailabilityRate,
}: LoanAccountStats) => {
  return (
    <StatsPanelWrapper
      graph={
        underMarginStatusCount > 0 &&
        excessMarginStatusCount > 0 &&
        nearMarginStatusCount > 0 ? (
          <PieChart width={320} height={400}>
            <Pie
              width={160}
              height={160}
              data={[
                {
                  name: `${underMarginStatusCount} ${handlePlural({
                    count: underMarginStatusCount,
                    singular: 'Under Margin Account',
                    plural: 'Under Margin Accounts',
                  })}`,
                  value: underMarginStatusCount,
                  fill: colors.red[200],
                },
                {
                  name: `${nearMarginStatusCount} ${handlePlural({
                    count: nearMarginStatusCount,
                    singular: 'Near Margin Account',
                    plural: 'Near Margin Accounts',
                  })}`,
                  value: nearMarginStatusCount,
                  fill: colors.orange[200],
                },
                {
                  name: `${excessMarginStatusCount} ${handlePlural({
                    count: excessMarginStatusCount,
                    singular: 'Excess Margin Account',
                    plural: 'Excess Margin Accounts',
                  })}`,
                  value: excessMarginStatusCount,
                  fill: colors.green[200],
                },
              ]}
              dataKey="value"
              nameKey="name"
            />
            <Legend
              verticalAlign="bottom"
              layout="vertical"
              iconType="circle"
              iconSize={8}
              formatter={(value) => (
                <Text color={colors.black[70]} display="inline-block" ml="8px">
                  {value}
                </Text>
              )}
            />
          </PieChart>
        ) : null
      }
      cardsSection={
        <Flex flexDirection="column" w="100%" h="400px" justify="space-evenly">
          <HStack w="100%" spacing="16px">
            <InfoCard
              title="Total Loan Outstanding"
              value={formatCurrencyAmount(
                totalOutstandingBalance,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              w="100%"
            />

            <InfoCard
              title="Number of Loan Accounts"
              value={String(loanAccountsCount)}
              icon={<Icon icon="hashtag" size="24px" />}
              w="100%"
            />
          </HStack>
          <HStack w="100%" spacing="16px">
            <InfoCard
              title="Total Margin Availability"
              value={formatCurrencyAmount(
                totalMarginAvailability,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              w="100%"
            />
            <InfoCard
              title="Total Margin Availability Rate"
              value={percentageToPercentageLabel(
                totalMarginAvailabilityRate * 100,
              )}
              icon={<Icon icon="percentage" size="24px" />}
              w="100%"
            />
          </HStack>
        </Flex>
      }
    />
  );
};

// TODO make props required
export const LoanAndCollateralAccountsStats = ({
  collateralStats,
  loanStats,
}: LoanAndCollateralAccountsStatsComponentProps) => {
  const [selectedTab, setSelectedTab] = React.useState<TabType>(
    loanStats ? 'loanAccounts' : 'collateralAccounts',
  );
  return (
    <Box w="100%">
      {collateralStats && loanStats && (
        <HStack w="100%" mb="40px" spacing="16px">
          <Button
            colorScheme={selectedTab === 'loanAccounts' ? 'aurora' : undefined}
            onClick={() => setSelectedTab(LOAN_ACCOUNTS)}
          >
            Loan Accounts
          </Button>
          <Button
            colorScheme={
              selectedTab === 'collateralAccounts' ? 'aurora' : undefined
            }
            onClick={() => setSelectedTab(COLLATERAL_ACCOUNTS)}
          >
            Collateral Accounts
          </Button>
        </HStack>
      )}
      {selectedTab === LOAN_ACCOUNTS && loanStats && (
        <LoanAccountsPanel {...loanStats} />
      )}
      {selectedTab === COLLATERAL_ACCOUNTS && (
        <CollateralAccountsPanel
          assigned={collateralStats.assigned}
          unassigned={collateralStats.unassigned}
        />
      )}
    </Box>
  );
};
